/* ==========================================================================
   Rook UI Core — Toggle & Toggle Group Controllers
   Web Components: <rk-toggle-element> & <rk-toggle-group>
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkToggleElement extends RkComponent {
    static hydration = 'eager';

    setup() {
        this.button = this.querySelector('button');
        if (!this.button) return;

        if (!this.button.hasAttribute('aria-pressed')) {
            this.button.setAttribute('aria-pressed', 'false');
        }

        this.delegate({
            'click button': this.onClick,
            'keydown button': this.onKeyDown,
        });
    }

    get isPressed() {
        return this.button?.getAttribute('aria-pressed') === 'true';
    }

    setPressed(value) {
        if (!this.button || this.button.disabled) return;

        const state = value ? 'true' : 'false';
        this.button.setAttribute('aria-pressed', state);

        if (value) {
            this.button.classList.add('rk-toggle--on');
            this.button.classList.remove('rk-toggle--off');
        } else {
            this.button.classList.add('rk-toggle--off');
            this.button.classList.remove('rk-toggle--on');
        }

        this.rkEmit('toggle:change', { pressed: value, value: this.dataset.value });
    }

    toggle() {
        this.setPressed(!this.isPressed);
    }

    onClick(event) {
        const group = this.closest('rk-toggle-group');
        if (!group) this.toggle();
    }

    onKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.button.click();
        }
    }
}

class RkToggleGroup extends RkComponent {
    static hydration = 'eager';

    setup() {
        this.type = this.dataset.type || 'single';

        if (!this.hasAttribute('role')) {
            this.setAttribute('role', 'group');
        }

        this.delegate({
            'rk:toggle:change': this.onToggleChange,
        });
    }

    get toggles() {
        return Array.from(this.querySelectorAll('rk-toggle-element'));
    }

    onToggleChange(event) {
        event.stopPropagation();

        const clickedToggle = event.target;
        const isNowPressed = clickedToggle.isPressed;

        clickedToggle.setPressed(!isNowPressed);

        if (this.type === 'single') {
            this.#handleSingle(clickedToggle);
        } else {
            this.#handleMultiple(clickedToggle);
        }
    }

    #handleSingle(toggle) {
        const wasPressed = toggle.isPressed;
        this.toggles.forEach((t) => t.setPressed(false));
        if (!wasPressed) toggle.setPressed(true);
        this.#emitChange();
    }

    #handleMultiple(toggle) {
        toggle.setPressed(!toggle.isPressed);
        this.#emitChange();
    }

    #emitChange() {
        const selected = this.toggles
            .filter((t) => t.isPressed)
            .map((t) => t.dataset.value);

        const value = this.type === 'single' ? (selected[0] || null) : selected;
        this.rkEmit('toggle-group:change', { value });
    }
}

if (!customElements.get('rk-toggle-element')) {
    customElements.define('rk-toggle-element', RkToggleElement);
}
if (!customElements.get('rk-toggle-group')) {
    customElements.define('rk-toggle-group', RkToggleGroup);
}
